// 認証 Context:起動時に /api/auth/me を叩いて user を確定する

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { ApiError, subscribeAuthEvents, tryRefresh } from "../api/client";
import { authApi, type LoginResponse, type MeResponse } from "../api/auth";

interface AuthState {
  user: MeResponse["user"] | null;
  tenant: MeResponse["tenant"] | null;
  sessionId: string | null;
  loading: boolean;
}

interface AuthContextValue extends AuthState {
  /** 2026-08-17〜:tenantCode 引数は廃止。呼出し側が残っていても無視。 */
  login: (
    email: string,
    password: string,
    /** @deprecated 後方互換のため型だけ残す。backend は tenant_code を見ない。 */
    tenantCode?: string
  ) => Promise<LoginResponse>;
  verifyLogin: (challengeId: string, code: string) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  logoutAll: () => Promise<void>;
  refreshMe: () => Promise<void>;
  /** Stage 2C T9:user.roles に "system_admin" を含むか */
  isSystemAdmin: boolean;
  /** Stage 3C:user.roles に 指定 role 集合のいずれかを含むか */
  hasAnyRole: (roles: string[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function getOrCreateDeviceFingerprint(): string {
  const KEY = "auth_demo_device_fp";
  let fp = localStorage.getItem(KEY);
  if (!fp) {
    fp = `fp_${Math.random().toString(36).slice(2)}_${Date.now()}`;
    localStorage.setItem(KEY, fp);
  }
  return fp;
}

function getDeviceName(): string {
  const ua = navigator.userAgent;
  if (ua.includes("Edg")) return "Edge";
  if (ua.includes("Chrome")) return "Chrome";
  if (ua.includes("Safari")) return "Safari";
  if (ua.includes("Firefox")) return "Firefox";
  return "Unknown Browser";
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    tenant: null,
    sessionId: null,
    loading: true,
  });

  const refreshMe = useCallback(async () => {
    try {
      const me = await authApi.me();
      setState({
        user: me.user,
        tenant: me.tenant,
        sessionId: me.session.id,
        loading: false,
      });
    } catch (e) {
      // 2026-07-06 P0 修复:401 时先尝试一次 refresh(可能 access_token 已过期但 refresh cookie 有效)
      // 原逻辑:401 直接 setState user=null → 永远跳 /login
      if (e instanceof ApiError && e.status === 401) {
        // 1) 先调一次 /api/auth/refresh 拿新 access_token
        const refreshed = await tryRefresh();
        if (refreshed) {
          // 2) refresh 成功 → 重新调 /me 拿 user
          try {
            const me2 = await authApi.me();
            setState({
              user: me2.user,
              tenant: me2.tenant,
              sessionId: me2.session.id,
              loading: false,
            });
            return;
          } catch (e2) {
            console.error("[AuthProvider] /me after refresh failed:", e2);
            // fall through to logout
          }
        }
        // 3) refresh 失败 或 /me 仍失败 → 未ログイン扱い
        setState({ user: null, tenant: null, sessionId: null, loading: false });
      } else {
        console.error("[AuthProvider] /me failed:", e);
        setState({ user: null, tenant: null, sessionId: null, loading: false });
      }
    }
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  // apiFetch 内の refresh 失敗イベントを購読し、user state を破棄する。
  // ProtectedRoute が user=null を検知して /login へリダイレクトする責務はそちらに任せる。
  useEffect(() => {
    const unsubscribe = subscribeAuthEvents(() => {
      setState({ user: null, tenant: null, sessionId: null, loading: false });
    });
    return unsubscribe;
  }, []);

  const login = useCallback(
    async (email: string, password: string, _legacyTenantCode?: string) => {
      // 2026-08-17〜:email+password のみ。tenantCode は引数互換のため受け取るが
      // request body には載せない(backend も無視する)。
      const resp = await authApi.login({
        email,
        password,
        device_fingerprint: getOrCreateDeviceFingerprint(),
        device_name: getDeviceName(),
      });
      // 成功時のみ /me を取って user を確定
      if (resp.status === "authenticated") {
        await refreshMe();
      }
      return resp;
    },
    [refreshMe]
  );

  const verifyLogin = useCallback(
    async (challengeId: string, code: string) => {
      const resp = await authApi.verifyLogin({
        challenge_id: challengeId,
        code,
        device_fingerprint: getOrCreateDeviceFingerprint(),
        device_name: getDeviceName(),
      });
      if (resp.status === "authenticated") {
        await refreshMe();
      }
      return resp;
    },
    [refreshMe]
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (e) {
      console.warn("[AuthProvider] logout failed:", e);
    } finally {
      setState({ user: null, tenant: null, sessionId: null, loading: false });
    }
  }, []);

  const logoutAll = useCallback(async () => {
    try {
      await authApi.logoutAll();
    } catch (e) {
      console.warn("[AuthProvider] logoutAll failed:", e);
    } finally {
      setState({ user: null, tenant: null, sessionId: null, loading: false });
    }
  }, []);

  const isSystemAdmin = state.user?.roles?.includes("system_admin") ?? false;

  // Stage 3C:多角色判定。例:hasAnyRole(["system_admin","editor"])
  const hasAnyRole = useCallback(
    (roles: readonly string[]): boolean => {
      const have: readonly string[] = state.user?.roles ?? [];
      return roles.some((r) => have.includes(r));
    },
    [state.user]
  );

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, login, verifyLogin, logout, logoutAll, refreshMe, isSystemAdmin, hasAnyRole }),
    [state, login, verifyLogin, logout, logoutAll, refreshMe, isSystemAdmin, hasAnyRole]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}